import { useContext, useMemo } from 'react';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';

export const _matchResultSourceTypes = {
  matchDay: 'matchDay',
  official: 'official',
  realtime: 'realtime',
}

/**
 * useMatchResults
 *
 * Purpose:
 * Resolve the "best" version of match results from multiple possible sources.
 *
 * Possible sources:
 * - officialResults   → final / authoritative (highest priority)
 * - realtimeResults   → live updates during the match
 * - matchDayResults   → user-submitted / temporary data (lowest priority)
 *
 * This hook:
 * - pulls all 3 sources from context
 * - finds the match-specific data for each source
 * - determines which one should be used
 * - returns both the resolved result AND metadata about where it came from
 */
export const useMatchResults = ({ match_true }) => {

  const { _itemKey, results, _results } = match_true ?? {}

  // 🧠 Access global sports season state from context
  const sportsSeasonContext = useContext(SportsSeasonContext);
  const { sportsSeason_state } = sportsSeasonContext ?? {};

  // 📦 Extract the 3 possible result sources
  const { realtimeResults, matchDayResults } = sportsSeason_state ?? {};

  // 🔍 Pull the specific match result from each source
  const results_official = results;
  const results_realtime = realtimeResults?.[_itemKey];
  const results_matchDay = matchDayResults?.[_itemKey];

  const rtrs = useMemo(() => {
    if (!results_realtime) return {}
    const mrz = {}
    // console.log('results_realtime', results_realtime)
    _.forEach(results_realtime, (rtr, uid) => {
      const { displayName, results, setNumber } = rtr ?? {}
      const { score, scores } = results ?? {}
      mrz[uid] = { displayName, setNumber, score, scores }
    })
    return mrz
  }, [results_realtime])


  /**
   * 🧩 Resolution Logic (PRIORITY ORDER)
   *
   * We decide which result to show based on availability.
   *
   * Priority:
   * 1. official (final, trusted)
   * 2. realtime (live game updates)
   * 3. matchDay (user-entered / fallback)
   *
   * NOTE: This logic can evolve later (ex: check "isFinal", timestamps, etc.)
   */
  let results_match = null;
  let result_source = null;
  let hasScore = false;
  let hasScores = false

  if (results_official) {
    const { score, scores } = results_official
    results_match = { _results: results_official }
    hasScore = score ? true : false
    hasScores = scores ? true : false
    result_source = _matchResultSourceTypes.official;
  } else if (results_realtime) {
    const { results } = results_realtime
    results_match = results;
    if (results_match) {
      const { score, scores } = results_match
      delete results_match.results;
      hasScore = score ? true : false
      hasScores = scores ? true : false
    }
    result_source = _matchResultSourceTypes.realtime;
  } else if (results_matchDay) {
    results_match = results_matchDay;
    result_source = _matchResultSourceTypes.realtime;
  }

  // if (results_realtime) {
  //   console.log('realtimeResults', realtimeResults)
  //   console.log('results_realtime !!!', results_realtime)
  //   console.log('rtrs', rtrs)
  // }


  return {
    results_match,
    result_source,
    results_realtime,
    rtrs,
    hasScore,
    hasScores,
    sources: {
      official: results_official,
      realtime: results_realtime,
      matchDay: results_matchDay
    }
  };
};